import { useEffect, useRef, useState } from 'react';

function readStoredNumber(storageKey) {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
  } catch (error) {
    return null;
  }
}

function writeStoredNumber(storageKey, value) {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, String(value));
  } catch (error) {
    // private mode 등
  }
}

/**
 * 목표 숫자까지 ease-out으로 순차 증가/감소.
 * storageKey가 있으면 마지막 표시값을 localStorage에 기억해,
 * 앱 완전 종료 후 재실행해도 이전 값 → 새 값(증가분)만 애니메이션한다.
 */
export function useAnimatedNumber(target, { durationMs = 1000, enabled = true, storageKey } = {}) {
  const safeTarget = Number.isFinite(Number(target)) ? Math.max(0, Math.trunc(Number(target))) : 0;
  const storedInitial = readStoredNumber(storageKey);
  const [value, setValue] = useState(() => (storedInitial != null ? storedInitial : safeTarget));
  const valueRef = useRef(storedInitial != null ? storedInitial : safeTarget);
  const hasBaselineRef = useRef(storedInitial != null);
  const frameRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      valueRef.current = safeTarget;
      setValue(safeTarget);
      writeStoredNumber(storageKey, safeTarget);
      return undefined;
    }

    const from = valueRef.current;
    const to = safeTarget;
    if (from === to) {
      writeStoredNumber(storageKey, to);
      hasBaselineRef.current = true;
      return undefined;
    }

    // HC 응답 전 target=0이 오면 기존 표시값을 유지 (0부터 다시 카운트업 방지)
    if (to === 0 && from > 0) {
      return undefined;
    }

    // 기준값이 없는 첫 HC 반영·감소: 0→N 카운트업 연출 없이 스냅
    const shouldSnap = !hasBaselineRef.current || to < from;
    if (shouldSnap) {
      cancelAnimationFrame(frameRef.current);
      valueRef.current = to;
      setValue(to);
      writeStoredNumber(storageKey, to);
      hasBaselineRef.current = true;
      return undefined;
    }

    const delta = Math.abs(to - from);
    const duration = Math.min(1400, Math.max(500, durationMs * Math.min(1.6, delta / 80)));
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      const next = Math.round(from + (to - from) * eased);
      valueRef.current = next;
      setValue(next);
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        valueRef.current = to;
        setValue(to);
        writeStoredNumber(storageKey, to);
        hasBaselineRef.current = true;
      }
    };

    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [safeTarget, durationMs, enabled, storageKey]);

  return value;
}
